import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { TourService, TourModule, TourStepItem } from '@app/services/tour.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-guide-drawer',
  templateUrl: './guide-drawer.component.html',
  styleUrls: ['./guide-drawer.component.scss'],
  standalone: false
})
export class GuideDrawerComponent implements OnInit, OnDestroy {
  isOpen: boolean = false;
  selectedModule: TourModule | null = null;
  searchQuery: string = '';
  completedStepIds: Set<string> = new Set();
  
  private subs = new Subscription();

  constructor(
    public tourService: TourService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.subs.add(
      this.tourService.isGuideDrawerOpen$.subscribe(open => {
        this.isOpen = open;
        this.cdr.markForCheck();
      })
    );

    this.subs.add(
      this.tourService.selectedModuleId$.subscribe(modId => {
        if (modId) {
          const found = this.tourService.modules.find(m => m.id === modId);
          this.selectedModule = found || null;
        } else {
          this.selectedModule = null;
        }
        this.cdr.markForCheck();
      })
    );

    this.subs.add(
      this.tourService.completedStepIds$.subscribe(ids => {
        this.completedStepIds = ids;
        this.cdr.markForCheck();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  get modules(): TourModule[] {
    return this.tourService.modules;
  }

  get filteredModules(): TourModule[] {
    if (!this.searchQuery || !this.searchQuery.trim()) {
      return this.modules;
    }
    const q = this.searchQuery.trim().toLowerCase();
    return this.modules.filter(m => 
      m.title.toLowerCase().includes(q) ||
      m.description.toLowerCase().includes(q) ||
      m.tag.toLowerCase().includes(q) ||
      m.steps.some(s => s.title.toLowerCase().includes(q) || s.description.toLowerCase().includes(q))
    );
  }

  get overallProgress(): { completed: number; total: number; percentage: number } {
    return this.tourService.getOverallProgress();
  }

  getModuleProgress(moduleId: string): { completed: number; total: number; percentage: number } {
    return this.tourService.getModuleProgress(moduleId);
  }

  getModuleStatus(module: TourModule): 'not_started' | 'in_progress' | 'completed' {
    const prog = this.getModuleProgress(module.id);
    if (prog.completed === 0) return 'not_started';
    if (prog.completed === prog.total) return 'completed';
    return 'in_progress';
  }

  isStepCompleted(stepId: string): boolean {
    return this.completedStepIds.has(stepId);
  }

  selectModule(module: TourModule): void {
    this.searchQuery = '';
    this.tourService.selectModule(module.id);
  }

  backToModules(): void {
    this.tourService.selectModule(null);
  }

  close(): void {
    this.tourService.closeGuideDrawer();
  }

  launchStep(step: TourStepItem): void {
    if (!this.selectedModule) return;
    this.tourService.launchStep(this.selectedModule.id, step.id);
  }

  launchFullModuleTour(module: TourModule): void {
    this.tourService.launchModuleTour(module.id);
  }

  resetProgress(): void {
    this.tourService.resetTourProgress();
  }
}
